// TES-925 — open BankID on the device the user is already on.
// TES-965 — shared by the patient web login and admin (was web-only).
//
// The login step showed only a QR code. On a phone with BankID that is a dead
// end: you cannot scan your own screen. The order's `autoStartToken` was in
// the init response all along; this turns it into the launch link.
//
// Per BankID's relying-party guidelines:
//   · phones and tablets: the universal link https://app.bankid.com/ —
//     opens the app when it is installed, and BankID's own help page when it
//     is not, instead of a silent failure of a custom scheme;
//   · computers: bankid:/// — the scheme the BankID desktop app registers.
//
// `redirect`: TES-925 sent `null` everywhere, because a redirect back to the
// page reloaded it and the page started a NEW order on load. TES-1088: on
// iOS that left her inside the BankID app. BankID's autostart guide: "On
// iOS, Redirect=null means that your web or app won't be launched once the
// order is completed." Many never switched back by hand, so the page never
// polled again and the signed order was swept as abandoned.
//
// So `bankIdReturnUrl` picks the redirect the way BankID's own reference
// client on developers.bankid.com does: on iOS, Safari gets the page URL
// back (with a fragment the page uses to resume the same order instead of
// starting a new one), Chrome and Firefox get their own app schemes, which
// reopen the browser on the tab she left. Android and computers keep `null`:
// there the BankID app closes and the browser behind it returns by itself.

export function isMobileDevice(userAgent: string, maxTouchPoints = 0): boolean {
  if (/Android|iPhone|iPad|iPod/i.test(userAgent)) return true;
  // iPadOS reports a desktop Safari user agent; touch points give it away.
  return /Macintosh/i.test(userAgent) && maxTouchPoints > 1;
}

function isIos(userAgent: string, maxTouchPoints: number): boolean {
  return (
    /iPhone|iPad|iPod/i.test(userAgent) ||
    (/Macintosh/i.test(userAgent) && maxTouchPoints > 1)
  );
}

// In-app browsers (Instagram, Facebook, LinkedIn, TikTok, Snapchat, the
// Google app) and other iOS browsers without a scheme BankID documents. An
// https return URL would open Safari instead of the app she came from, so
// these keep `null` and she switches back by hand.
const IOS_NO_RETURN =
  /FBAN|FBAV|FBIOS|Instagram|LinkedInApp|TikTok|musical_ly|Snapchat|GSA\/|EdgiOS|OPiOS|YaBrowser|DuckDuckGo/i;

/**
 * The `redirect` BankID opens when the order completes, or `"null"` for none.
 * `pageUrl` is where she should land: the exact URL of the tab, including the
 * resume fragment.
 */
export function bankIdReturnUrl(
  userAgent: string,
  pageUrl: string,
  maxTouchPoints = 0,
): string {
  if (!isIos(userAgent, maxTouchPoints)) return "null";
  if (/CriOS/i.test(userAgent)) return "googlechrome://";
  if (/FxiOS/i.test(userAgent)) return "firefox://";
  if (IOS_NO_RETURN.test(userAgent)) return "null";
  return pageUrl;
}

/**
 * TES-1622 — the link a phone browser opens BY ITSELF, without a tap.
 *
 * Always the bankid:/// scheme, also on phones. The universal link is only
 * handed to the BankID app from a real tap on a real link; opened by script,
 * iOS loads app.bankid.com in the tab instead and the sign-in page is gone.
 * An unhandled custom scheme leaves the page where it is: iOS Safari asks
 * "Open in BankID?", and a browser that refuses a launch without a tap
 * (Chrome on Android) simply does nothing, so the tap link stays the way in.
 * `redirect` follows `bankIdReturnUrl` exactly as for the tap.
 */
export function bankIdAutostartUrl(
  autoStartToken: string,
  redirect = "null",
): string {
  return bankIdLaunchUrl(autoStartToken, false, redirect);
}

export function bankIdLaunchUrl(
  autoStartToken: string,
  mobile: boolean,
  redirect = "null",
): string {
  const token = encodeURIComponent(autoStartToken);
  const back = redirect === "null" ? "null" : encodeURIComponent(redirect);
  return mobile
    ? `https://app.bankid.com/?autostarttoken=${token}&redirect=${back}`
    : `bankid:///?autostarttoken=${token}&redirect=${back}`;
}
